import { useCallback, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  type User,
} from 'firebase/auth';
import { AuthLayout, Input } from '@react-apps/design-system';
import {
  ForcedPasswordChangeForm,
  reauthenticateAndChangePassword,
} from '@react-apps/auth';
import type { ChangePasswordInput } from '@react-apps/auth';
import { firebaseAuth } from '@/lib/firebase';
import { apiClient, extractApiErrorCode } from '@/lib/api-client';
import { fetchSsoTarget, issueSsoCode } from '@/lib/sso-api';
import { VerificationWall } from '@/modules/auth/presentation/components/VerificationWall';

/**
 * Login global — SSO **iniciado pelo destino** ("Entrar com sua conta Primum").
 * Rota pública de `conta.primum.edu.br` (o user-dashboard é só o host); os gates
 * de conta são resolvidos no scope do **destino** (o `x-scope-id` das chamadas é
 * o `scope_id` da query, não o do host — ver `sso-api.ts` e o §4.1 do plano).
 *
 * Fluxo: valida o destino → autentica (sessão existente ou e-mail+senha) →
 * `POST /auth/sso/codes` → redireciona para a `redirectUrl` que o servidor
 * montou. Recusa de conta (verificação, troca de senha) resolve aqui, na tela;
 * recusa de negócio (sem cadastro, acesso inativo) volta ao app de origem por
 * `redirect_uri?error=…`.
 */

type Phase =
  | 'loading'
  | 'sign-in'
  | 'submitting'
  | 'verify-wall'
  | 'password-change'
  | 'fatal';

interface SsoParams {
  scopeId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
}

function readParams(): SsoParams | null {
  const q = new URLSearchParams(window.location.search);
  const scopeId = q.get('scope_id');
  const redirectUri = q.get('redirect_uri');
  const state = q.get('state');
  const codeChallenge = q.get('code_challenge');
  if (!scopeId || !redirectUri || !state || !codeChallenge) return null;
  return { scopeId, redirectUri, state, codeChallenge };
}

export function AuthorizePage() {
  const [params] = useState<SsoParams | null>(() => readParams());
  const [phase, setPhase] = useState<Phase>('loading');
  const [platformName, setPlatformName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(() => firebaseAuth.currentUser);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // A senha em mão sobrevive a um PASSWORD_CHANGE_REQUIRED — é a temporária que
  // o formulário de troca reautentica.
  const passwordRef = useRef('');

  // Recusa de negócio: volta ao app de origem com ?error=. Só é seguro depois de
  // o destino ter sido validado (o GET target já validou o redirect_uri).
  const redirectWithError = useCallback(
    (code: string) => {
      if (!params) return;
      const url = new URL(params.redirectUri);
      url.searchParams.set('error', code);
      url.searchParams.set('state', params.state);
      window.location.assign(url.toString());
    },
    [params],
  );

  /** Emite o code e redireciona; mapeia as recusas. `temp` = senha em mão, se houver. */
  const runCodes = useCallback(
    async (temp?: string) => {
      if (!params) return;
      setPhase('submitting');
      setMessage(null);
      try {
        const { redirectUrl } = await issueSsoCode(params);
        window.location.assign(redirectUrl);
      } catch (err) {
        const code = extractApiErrorCode(err);
        switch (code) {
          case 'ACCOUNT_NOT_ACTIVATED':
            setPhase('verify-wall');
            return;
          case 'PASSWORD_CHANGE_REQUIRED':
            if (temp) {
              passwordRef.current = temp;
              setPhase('password-change');
            } else {
              // Sessão já viva sem a temporária em mão: não dá para reautenticar
              // aqui. Manda resolver na conta.
              setPhase('fatal');
              setMessage(
                'Sua senha precisa ser trocada. Acesse sua conta Primum, troque a senha e tente novamente.',
              );
            }
            return;
          case 'INVALID_SSO_REDIRECT_URI':
            // Não redirecionar para um destino não validado.
            setPhase('fatal');
            setMessage('Este aplicativo não é um destino de login válido.');
            return;
          default:
            // Recusa de negócio (sem cadastro, acesso inativo, etc.): devolve ao
            // app de origem, que decide (mensagem ou cadastro dele).
            redirectWithError(code ?? 'ACCESS_DENIED');
            return;
        }
      }
    },
    [params, redirectWithError],
  );

  // Valida o destino no mount (é a checagem de UX; a que vale é a do /codes).
  useEffect(() => {
    if (!params) {
      setPhase('fatal');
      setMessage('Link de login incompleto.');
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const target = await fetchSsoTarget(params.scopeId, params.redirectUri);
        if (!alive) return;
        setPlatformName(target.platformName);
        setPhase(firebaseAuth.currentUser ? 'submitting' : 'sign-in');
        if (firebaseAuth.currentUser) {
          // Sessão já viva: segue direto, sem pedir senha.
          void runCodes();
        }
      } catch (err) {
        if (!alive) return;
        const code = extractApiErrorCode(err);
        setPhase('fatal');
        setMessage(
          code === 'INVALID_SSO_REDIRECT_URI'
            ? 'Este aplicativo não é um destino de login válido.'
            : 'Não foi possível iniciar o login. Tente novamente.',
        );
      }
    })();
    return () => {
      alive = false;
    };
    // Só no mount: params é congelado no primeiro render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return onAuthStateChanged(firebaseAuth, setUser);
  }, []);

  const handleSignIn = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setPhase('submitting');
    setMessage(null);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      passwordRef.current = password;
      await runCodes(password);
    } catch (err) {
      // Erro do Firebase (senha errada) ou da emissão.
      setPhase('sign-in');
      setMessage(
        extractApiErrorCode(err) === 'ACCOUNT_NOT_FOUND'
          ? 'Este e-mail não está cadastrado.'
          : 'E-mail ou senha inválidos.',
      );
    }
  };

  const handlePasswordChange = async (input: ChangePasswordInput) => {
    setMessage(null);
    try {
      const resolvedEmail = await reauthenticateAndChangePassword({
        auth: firebaseAuth,
        input,
        changePassword: ({ newPassword }) =>
          apiClient
            .post('/auth/change-password', { newPassword })
            .then(() => {}),
      });
      // O back revoga todas as sessões: re-loga com a senha nova e segue.
      await signInWithEmailAndPassword(
        firebaseAuth,
        resolvedEmail || email,
        input.newPassword,
      );
      passwordRef.current = input.newPassword;
      await runCodes(input.newPassword);
    } catch {
      setMessage('Não foi possível trocar a senha. Tente novamente.');
    }
  };

  const switchAccount = useCallback(async () => {
    await firebaseAuth.signOut();
    setPassword('');
    passwordRef.current = '';
    setMessage(null);
    setPhase('sign-in');
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  if (phase === 'fatal') {
    return (
      <AuthLayout title="Login" subtitle="">
        <p className="text-(--color-text-secondary) text-sm">
          {message ?? 'Não foi possível concluir o login.'}
        </p>
      </AuthLayout>
    );
  }

  if (phase === 'verify-wall') {
    return (
      <VerificationWall
        email={user?.email ?? email}
        onContinue={() => runCodes(passwordRef.current || undefined)}
        onBack={() => void switchAccount()}
        isBusy={false}
      />
    );
  }

  if (phase === 'password-change') {
    return (
      <AuthLayout
        title="Troque sua senha"
        subtitle="Sua senha é temporária e precisa ser trocada para continuar."
      >
        <ForcedPasswordChangeForm
          tempPassword={passwordRef.current}
          isLoading={false}
          error={message}
          onSubmit={(input) => void handlePasswordChange(input)}
        />
      </AuthLayout>
    );
  }

  if (phase === 'loading' || phase === 'submitting') {
    return (
      <div className="bg-(--color-bg-secondary) flex min-h-screen items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="border-(--color-accent) h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-(--color-text-secondary) text-sm">
            {platformName ? `Entrando na ${platformName}…` : 'Carregando…'}
          </p>
        </div>
      </div>
    );
  }

  // phase === 'sign-in'
  const subtitle = platformName
    ? `Entre com sua conta Primum para acessar ${platformName}`
    : 'Entre com sua conta Primum';

  return (
    <AuthLayout title="Entrar com sua conta Primum" subtitle={subtitle}>
      {user ? (
        <div className="space-y-4">
          <p className="text-(--color-text-secondary) text-sm">
            Você está conectado como{' '}
            <strong className="text-(--color-text-primary)">
              {user.email}
            </strong>
            .
          </p>
          <button
            onClick={() => void runCodes()}
            className="bg-(--color-accent) w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Continuar {platformName ? `para ${platformName}` : ''}
          </button>
          <button
            onClick={() => void switchAccount()}
            className="text-(--color-accent) w-full text-sm hover:underline"
          >
            Entrar com outra conta
          </button>
        </div>
      ) : (
        <form onSubmit={handleSignIn} className="space-y-5">
          <Input
            id="email"
            label="E-mail"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
          />
          <Input
            id="password"
            label="Senha"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
          {message && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
              {message}
            </div>
          )}
          <button
            type="submit"
            className="bg-(--color-accent) w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Entrar
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
